import { useEffect, useRef, useState } from "react";
import { HandCoins, History, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import PinConfirm from "../components/PinConfirm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATUS_STYLE = { aktif: "bg-amber-500/10 text-amber-600 border-amber-500/20", lunas: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" };

export default function PiutangSaldo() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: "", amount: "", note: "" });
  const [pinOpen, setPinOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filterEmail, setFilterEmail] = useState(null);
  const [deductTarget, setDeductTarget] = useState(null);
  const [deductAmount, setDeductAmount] = useState("");
  const [deductBusy, setDeductBusy] = useState(false);
  const [histTarget, setHistTarget] = useState(null);
  const [users, setUsers] = useState([]);
  const [agenQuery, setAgenQuery] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const suggestRef = useRef(null);

  const load = () => api.get("/debts").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const close = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) { setSuggestOpen(false); setForceShow(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const qAgen = agenQuery.trim().toLowerCase();
  const agenList = users.filter((u) => !["admin", "admin_fasnet"].includes(u.role));
  const suggestions = qAgen
    ? agenList.filter((u) => [u.email, u.name, u.agent_code].some((f) => (f || "").toLowerCase().includes(qAgen))).slice(0, 8)
    : agenList.slice(0, 8);
  const showSug = suggestOpen && (qAgen.length >= 3 || forceShow);

  const pilihAgen = (u) => {
    setForm((f) => ({ ...f, email: u.email }));
    setAgenQuery(u.email);
    setSuggestOpen(false);
    setForceShow(false);
  };

  const clearAgen = () => {
    setAgenQuery("");
    setForm((f) => ({ ...f, email: "" }));
    setSuggestOpen(false);
    setForceShow(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/debts", { email: form.email.trim(), amount: Number(form.amount), note: form.note.trim() });
      toast.success(`Pinjaman ${rupiah(Number(form.amount))} diberikan ke ${form.email}`);
      setForm({ email: "", amount: "", note: "" });
      setAgenQuery("");
      setForceShow(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const settleTunai = async (id) => {
    try {
      await api.put(`/debts/${id}/settle`, { deduct: false });
      toast.success("Lunas — dibayar tunai");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openDeduct = (d) => { setDeductTarget(d); setDeductAmount(String(d.amount - (d.paid || 0))); };

  const doDeduct = async () => {
    setDeductBusy(true);
    try {
      const r = await api.post(`/debts/${deductTarget.id}/deduct`, { amount: Number(deductAmount) });
      toast.success(r.data.lunas ? `Lunas — saldo agen dipotong ${rupiah(r.data.potong)}` : `Angsuran ${rupiah(r.data.potong)} terpotong — sisa ${rupiah(r.data.sisa)}`);
      setDeductTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setDeductBusy(false); }
  };

  const doSettleAll = async () => {
    setDeductBusy(true);
    try {
      const r = await api.post("/debts/settle-all", { email: deductTarget.email });
      toast.success(`Seluruh piutang lunas — saldo dipotong ${rupiah(r.data.total)} (${r.data.count} piutang)`);
      setDeductTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setDeductBusy(false); }
  };

  const sisaOf = (d) => d.amount - (d.paid || 0);
  const totalAktif = list.filter((d) => d.status === "aktif").reduce((s, d) => s + sisaOf(d), 0);
  const perAgent = Object.values(
    list.filter((d) => d.status === "aktif").reduce((acc, d) => {
      const k = d.user_id;
      if (!acc[k]) acc[k] = { name: d.name || d.email, email: d.email, agent_code: d.agent_code || "", total: 0, count: 0 };
      acc[k].total += d.amount - (d.paid || 0);
      acc[k].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-6" data-testid="piutang-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><HandCoins className="w-7 h-7 text-amber-500" /> Hutang Saldo Agen</h1>
        <p className="text-sm text-muted-foreground mt-1">Beri saldo ke agen sebagai hutang (saldo langsung masuk, tercatat sebagai hutang agen). Total hutang aktif: <span className="font-bold font-mono text-amber-600" data-testid="piutang-total">{rupiah(totalAktif)}</span></p>
      </div>

      <Card className="bg-card border-border shadow-sm" data-testid="debt-form-card">
        <CardHeader><CardTitle className="text-base font-heading">Beri Saldo sebagai Hutang</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); setPinOpen(true); }} className="grid sm:grid-cols-4 gap-3">
            <div className="space-y-1.5 sm:col-span-2 relative" ref={suggestRef}>
              <Label>Cari Agen (email / nama / kode)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input type="email" required value={agenQuery}
                  onChange={(e) => { setAgenQuery(e.target.value); setForm({ ...form, email: e.target.value }); setSuggestOpen(true); setForceShow(false); }}
                  onFocus={() => setSuggestOpen(true)}
                  placeholder="ketik email, nama, atau kode agen..." className="pl-9 pr-16" data-testid="input-debt-email" autoComplete="off" />
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {agenQuery && (
                    <button type="button" title="Hapus pencarian" onClick={clearAgen}
                      className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-debt-search-clear">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button type="button" title="Cari agen" onClick={() => { setForceShow(true); setSuggestOpen(true); }}
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-debt-search">
                    <Search className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {showSug && suggestions.length > 0 && (
                <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg max-h-56 overflow-y-auto" data-testid="debt-agen-suggestions">
                  {suggestions.map((u) => (
                    <button key={u.id || u.email} type="button" onClick={() => pilihAgen(u)}
                      className="w-full px-3 py-2 text-left hover:bg-accent/60 transition-colors border-b border-border/50 last:border-0"
                      data-testid={`debt-agen-option-${u.email}`}>
                      <div className="text-xs font-medium truncate">{u.name || u.email}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">{u.email}{u.agent_code ? ` • ${u.agent_code}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
              {showSug && !suggestions.length && (
                <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg px-3 py-2 text-xs text-muted-foreground" data-testid="debt-agen-empty">Agen tidak ditemukan</div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Nominal</Label>
              <Input type="number" min="1000" step="1000" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="cth: 50000" data-testid="input-debt-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="cth: pinjam buat stok" data-testid="input-debt-note" />
            </div>
            <div className="sm:col-span-4">
              <Button type="submit" className="w-full" data-testid="btn-give-debt">Beri Saldo Hutang (PIN)</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {perAgent.length > 0 && (
        <Card className="bg-card border-border shadow-sm" data-testid="piutang-per-agen-card">
          <CardHeader><CardTitle className="text-base font-heading">Agen dengan Hutang Aktif ({perAgent.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {perAgent.map((a) => (
                <button key={a.email} type="button" onClick={() => setFilterEmail(filterEmail === a.email ? null : a.email)}
                  className={`w-full px-4 py-3 flex items-center justify-between gap-3 text-left transition-colors hover:bg-accent/60 ${filterEmail === a.email ? "bg-amber-500/10 border-l-2 border-amber-500" : "border-l-2 border-transparent"}`}
                  data-testid={`piutang-agen-${a.email}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center font-mono text-xs font-bold text-amber-500 shrink-0">{a.agent_code || (a.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{a.name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">{a.email} • {a.count} hutang</div>
                    </div>
                  </div>
                  <div className="font-mono font-bold text-amber-500 whitespace-nowrap" data-testid={`piutang-total-${a.email}`}>{rupiah(a.total)}</div>
                </button>
              ))}
              <div className="px-4 py-3 flex items-center justify-between bg-amber-500/5 border-t-2 border-amber-500/30" data-testid="piutang-grand-total">
                <div className="text-sm font-bold">Total Seluruh Hutang Agen</div>
                <div className="font-mono font-extrabold text-lg text-amber-600">{rupiah(totalAktif)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="debt-list-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading">Daftar Hutang Agen</CardTitle>
          {filterEmail && (
            <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => setFilterEmail(null)} data-testid="btn-clear-piutang-filter">
              Filter: {filterEmail} ✕
            </Button>
          )}
        </CardHeader>
        <Table className="min-w-[640px]" data-testid="debt-table">
          <TableHeader>
            <TableRow>
              <TableHead>Waktu</TableHead><TableHead>Agen</TableHead><TableHead className="text-right">Nominal</TableHead>
              <TableHead>Catatan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!list.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="debt-empty">Belum ada hutang agen</TableCell></TableRow>}
            {(filterEmail ? list.filter((d) => d.email === filterEmail) : list).map((d) => (
              <TableRow key={d.id} data-testid={`debt-row-${d.id}`}>
                <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(d.created_at)}</TableCell>
                <TableCell><div className="text-xs font-medium">{d.name || d.email}</div><div className="text-[10px] font-mono text-muted-foreground">{d.email}{d.agent_code ? ` • ${d.agent_code}` : ""}</div></TableCell>
                <TableCell className="text-right font-mono font-semibold text-xs whitespace-nowrap">
                  {rupiah(d.status === "aktif" ? sisaOf(d) : d.amount)}
                  {d.status === "aktif" && (d.paid || 0) > 0 && <div className="text-[9px] text-muted-foreground font-normal">dari {rupiah(d.amount)} • terangsur {rupiah(d.paid)}</div>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[180px]"><span className="line-clamp-2">{d.note || "-"}</span></TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border ${STATUS_STYLE[d.status] || ""}`} data-testid={`debt-status-${d.id}`}>{d.status === "lunas" ? `Lunas (${d.settle_method === "potong_saldo" ? "potong saldo" : d.settle_method === "koreksi" ? "koreksi" : "tunai"})` : "Aktif"}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="mr-1.5 px-2" title="Riwayat angsuran" onClick={() => setHistTarget(d)} data-testid={`btn-debt-history-${d.id}`}><History className="w-3.5 h-3.5" /></Button>
                  {d.status === "aktif" && (
                    <>
                      <Button size="sm" variant="outline" className="mr-1.5 text-emerald-600 border-emerald-500/30" onClick={() => openDeduct(d)} data-testid={`btn-debt-settle-potong-${d.id}`}>Potong Saldo</Button>
                      <Button size="sm" variant="ghost" onClick={() => settleTunai(d.id)} data-testid={`btn-debt-settle-tunai-${d.id}`}>Lunas Tunai</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!histTarget} onOpenChange={(o) => !o && setHistTarget(null)}>
        <DialogContent className="bg-card border-border sm:max-w-sm" data-testid="dialog-debt-history">
          <DialogHeader><DialogTitle className="text-base font-heading">Riwayat Angsuran — {histTarget?.name || histTarget?.email}</DialogTitle></DialogHeader>
          {histTarget && (
            <div className="space-y-3">
              <div className="rounded-lg bg-muted/50 border border-border px-3 py-2 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Nominal hutang</span><b className="font-mono">{rupiah(histTarget.amount)}</b></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Terangsur</span><b className="font-mono text-emerald-600" data-testid="history-total-paid">{rupiah(histTarget.paid || 0)}</b></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sisa</span><b className="font-mono text-amber-600">{rupiah(histTarget.status === "aktif" ? sisaOf(histTarget) : 0)}</b></div>
              </div>
              {(histTarget.installments || []).length > 0 ? (
                <div className="divide-y divide-border rounded-lg border border-border max-h-64 overflow-y-auto" data-testid="debt-history-list">
                  {[...(histTarget.installments || [])].reverse().map((h, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`debt-history-item-${i}`}>
                      <div>
                        <div className="font-mono font-semibold text-emerald-600">{rupiah(h.amount)}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDateTime(h.at)}{h.bulk ? " • pelunasan total" : h.method === "tunai" ? " • tunai" : " • potong saldo"}</div>
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground text-right shrink-0">oleh<br />{h.by}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4" data-testid="debt-history-empty">Belum ada angsuran tercatat</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PinConfirm open={pinOpen} onOpenChange={setPinOpen} title="Beri Saldo sebagai Hutang?" description={`Saldo ${rupiah(Number(form.amount) || 0)} akan masuk ke ${form.email} dan tercatat sebagai hutang agen.`} confirmLabel={busy ? "Memproses..." : "Ya, Beri Saldo"} onConfirm={submit} testid="pin-debt" />

      <Dialog open={!!deductTarget} onOpenChange={(o) => !o && setDeductTarget(null)}>
        <DialogContent className="bg-card border-border sm:max-w-sm" data-testid="dialog-potong-saldo">
          <DialogHeader><DialogTitle className="text-base font-heading">Potong Saldo — {deductTarget?.name || deductTarget?.email}</DialogTitle></DialogHeader>
          {deductTarget && (() => {
            const agenAktif = list.filter((x) => x.email === deductTarget.email && x.status === "aktif");
            const totalAgen = agenAktif.reduce((s, x) => s + sisaOf(x), 0);
            return (
              <div className="space-y-4">
                <div className="rounded-lg bg-muted/50 border border-border px-3 py-2 text-xs space-y-1" data-testid="deduct-info">
                  <div className="flex justify-between"><span className="text-muted-foreground">Sisa hutang ini</span><b className="font-mono">{rupiah(sisaOf(deductTarget))}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total hutang agen</span><b className="font-mono text-amber-600" data-testid="potong-total-agen">{rupiah(totalAgen)} ({agenAktif.length} piutang)</b></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Nominal Angsuran (Rp)</Label>
                  <Input type="number" min="1000" step="1000" value={deductAmount} onChange={(e) => setDeductAmount(e.target.value)} data-testid="input-deduct-amount" />
                </div>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doDeduct} disabled={deductBusy || !(Number(deductAmount) > 0)} data-testid="btn-deduct-submit">
                  {deductBusy ? "Memproses..." : `Potong ${rupiah(Number(deductAmount) || 0)}`}
                </Button>
                {agenAktif.length > 1 && (
                  <Button variant="outline" className="w-full text-amber-600 border-amber-500/30" onClick={doSettleAll} disabled={deductBusy} data-testid="btn-deduct-all">
                    Potong Sesuai Total Hutang Agen ({rupiah(totalAgen)})
                  </Button>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
